import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { api } from "../../api";
import "../../views/BookingFlow/BookingFlow.css";
import ConciergeLogo from "../../assets/images/conciergeLogoLight.svg";
import Modal from "../common/Modal";
import {
  selectedBookingDateToIso,
  computePublicHolidaySurchargeAmount,
  PUBLIC_HOLIDAY_SURCHARGE_NOTICE,
  publicHolidaysQueryForLocation,
} from "../../utils/bookingFlowPublicHolidays";
import { formatArrivalTime } from "../../utils/formatArrivalTime";

const Step2 = ({
  cartData,
  onStepComplete,
  onCartUpdate,
  onStepBack,
  isLoading,
}) => {
  const [services, setServices] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [hasManuallyNavigated, setHasManuallyNavigated] = useState(false);
  const [serviceError, setServiceError] = useState("");
  const [publicHolidayByDate, setPublicHolidayByDate] = useState({});

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    showCancel: false,
    onConfirm: null,
  });

  // Modal helper functions
  const showModal = (
    title,
    message,
    type = "info",
    showCancel = false,
    onConfirm = null
  ) => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      showCancel,
      onConfirm,
    });
  };

  const closeModal = () => {
    setModal((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    let cancelled = false;
    const phQuery = publicHolidaysQueryForLocation(
      cartData?.step1Data?.selectedLocation,
    );
    if (!phQuery) {
      setPublicHolidayByDate({});
      return undefined;
    }

    (async () => {
      try {
        const res = await api(`booking-flow/public-holidays?${phQuery}`, "GET");
        if (
          !cancelled &&
          res.data?.success &&
          res.data?.holidaysByDate &&
          typeof res.data.holidaysByDate === "object"
        ) {
          setPublicHolidayByDate(res.data.holidaysByDate);
        }
      } catch (e) {
        console.warn("Step2: could not load public holidays:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cartData?.step1Data?.selectedLocation]);

  // Function to clear cart cookie
  const clearCartCookie = () => {
    document.cookie =
      "ccw_booking_cart_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    console.log("Cart cookie cleared due to 404 error");
  };

  // Function to check URL params for codes
  const checkUrlParamsForCodes = useCallback(async (cartId) => {
    if (!cartId) return;
    
    const queryParams = new URLSearchParams(window.location.search);
    const offerCode = queryParams.get("offer");
    const couponCode = queryParams.get("coupon");
    const rewardCode = queryParams.get("reward");

    // Check offer code
    if (offerCode && (!cartData?.offerDetails || cartData.offerDetails.code !== offerCode)) {
      try {
        const response = await api(`offer/?code=${offerCode}`, "GET");
        if (response.status === 200 && response.data.offer !== null) {
          const offer = response.data.offer;
          const updateResponse = await api(`booking-flow/cart/${cartId}`, "PUT", {
            offerDetails: offer,
          });
          if (updateResponse.data.success && updateResponse.data.cart) {
            onCartUpdate(updateResponse.data.cart);
          }
        }
      } catch (error) {
        console.error("Error checking offer:", error);
      }
    }

    // Check coupon code
    if (couponCode && (!cartData?.couponDetails || cartData.couponDetails.code !== couponCode)) {
      try {
        const response = await api(`coupon/${couponCode}`, "GET");
        if (response.status === 200 && response.data.coupon !== null) {
          const coupon = response.data.coupon;
          const updateResponse = await api(`booking-flow/cart/${cartId}`, "PUT", {
            couponDetails: coupon,
          });
          if (updateResponse.data.success && updateResponse.data.cart) {
            onCartUpdate(updateResponse.data.cart);
          }
        }
      } catch (error) {
        console.error("Error checking coupon:", error);
      }
    }
  }, [cartData, onCartUpdate]);

  // Initialize selected services from cart data
  useEffect(() => {
    if (cartData?.step2Data?.selectedServices) {
      setSelectedServices(cartData.step2Data.selectedServices);
    }
    // Debug: Log cart data to check if offer is present
    console.log("Step2 - Cart data updated:", cartData);
    console.log("Step2 - Offer details:", cartData?.offerDetails);
    console.log("Step2 - Coupon details:", cartData?.couponDetails);
  }, [cartData]);

  // Check URL params for codes whenever cart data changes
  useEffect(() => {
    if (cartData?._id) {
      checkUrlParamsForCodes(cartData._id);
    }
  }, [cartData?._id, checkUrlParamsForCodes]);

  // Set the correct service index based on selected service
  // Auto-navigation only on initial load
  useEffect(() => {
    if (selectedServices.length > 0 && services && !hasManuallyNavigated) {
      const selectedService = selectedServices[0];
      const serviceGroups = getServiceGroups();

      console.log("DEBUG: Auto-navigation check (initial load only)");
      console.log("DEBUG: Selected service:", selectedService);
      console.log("DEBUG: Service groups:", serviceGroups);
      console.log("DEBUG: Current service index:", currentServiceIndex);

      // Find which group contains the selected service
      const groupIndex = serviceGroups.findIndex((group) => {
        console.log(
          `DEBUG: Checking group ${group.categoryName}:`,
          group.services.map((s) => ({ id: s.id, name: s.name }))
        );
        const found = group.services.some((service) => {
          const match = service.id === selectedService.id;
          console.log(
            `DEBUG: Service ${service.name} (${service.id}) matches selected (${selectedService.id}):`,
            match
          );
          return match;
        });
        console.log(
          `DEBUG: Group ${group.categoryName} contains selected service:`,
          found
        );
        return found;
      });

      console.log("DEBUG: Found group index:", groupIndex);

      if (groupIndex !== -1 && groupIndex !== currentServiceIndex) {
        console.log(
          `Setting service index to ${groupIndex} for selected service:`,
          selectedService.name
        );
        setCurrentServiceIndex(groupIndex);
      } else {
        console.log(
          "DEBUG: No change needed - groupIndex:",
          groupIndex,
          "currentServiceIndex:",
          currentServiceIndex
        );
      }
    } else {
      console.log(
        "DEBUG: Auto-navigation skipped - selectedServices:",
        selectedServices.length,
        "services:",
        !!services,
        "hasManuallyNavigated:",
        hasManuallyNavigated
      );
    }
  }, [selectedServices, services, currentServiceIndex, hasManuallyNavigated]);

  const loadServices = useCallback(
    async (locationId) => {
      if (!locationId) {
        console.log("No location ID provided, cannot load services");
        return;
      }

      setIsLoadingServices(true);
      try {
        // Build query parameters with vehicle type
        const params = new URLSearchParams();
        if (cartData?.step1Data?.vehicleType) {
          params.append("vehicleType", cartData.step1Data.vehicleType);
        }

        const queryString = params.toString();
        const endpoint = `booking-flow/services/${locationId}${
          queryString ? `?${queryString}` : ""
        }`;

        console.log(
          "Loading services for location:",
          locationId,
          "with vehicle type:",
          cartData?.step1Data?.vehicleType
        );

        const response = await api(endpoint, "GET");

        if (response.data.success) {
          setServices(response.data.services);
          console.log(
            "Services loaded for location:",
            locationId,
            "vehicle type:",
            cartData?.step1Data?.vehicleType,
            response.data.services
          );
        } else {
          console.error("Failed to load services:", response.data);
        }
      } catch (error) {
        console.error("Error loading services:", error);
        // Clear cookie if it's a 404 error (location not found)
        if (error.response?.status === 404) {
          clearCartCookie();
        }
      } finally {
        setIsLoadingServices(false);
      }
    },
    [cartData?.step1Data?.vehicleType]
  );

  // Load services when location or vehicle type changes or component mounts
  useEffect(() => {
    const locationId = cartData?.step1Data?.selectedLocationId;
    const vehicleType = cartData?.step1Data?.vehicleType;

    if (locationId) {
      console.log(
        "Loading services for location:",
        locationId,
        "vehicle type:",
        vehicleType
      );
      loadServices(locationId);
    } else {
      console.log("No location selected, clearing services");
      setServices(null);
    }
  }, [
    cartData?.step1Data?.selectedLocationId,
    cartData?.step1Data?.vehicleType,
    loadServices,
  ]);

  const updateCartStep2 = async (newSelectedServices) => {
    if (!cartData) return null;
    try {
      const response = await api(
        `booking-flow/cart/${cartData._id}/step2`,
        "PUT",
        { selectedServices: newSelectedServices }
      );
      if (response.data.success) {
        onCartUpdate(response.data.cart);
        return response.data.cart;
      }
    } catch (error) {
      console.error("Error updating step 2:", error);
      // Clear cookie if it's a 404 error (cart not found)
      if (error.response?.status === 404) {
        clearCartCookie();
      }
    }
    return null;
  };

  const handleServiceSelect = (service, category) => {
    setSelectedServices((prev) => {
      // Clear all existing services to ensure only one is selected
      const newServices = [
        {
          id: service.id,
          name: service.name,
          price: service.price,
          duration: service.duration,
          category: category,
        },
      ];

      console.log(
        "Service selected:",
        service.name,
        "Category:",
        category,
        "Cleared all previous selections"
      );

      // Update cart
      updateCartStep2(newServices);

      return newServices;
    });
  };

  const handleContinue = async () => {
    if (selectedServices.length === 0) {
      setServiceError("Please select at least one service to continue.");
      showModal(
        "Missing Selection",
        "Please select at least one service to continue.",
        "warning"
      );
      
      // Scroll to service selection
      setTimeout(() => {
        const serviceSection = document.querySelector(".service-selection");
        if (serviceSection) {
          serviceSection.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }
    
    // Clear error if validation passes
    setServiceError("");

    // Update cart with selected services before completing step
    const updatedCart = await updateCartStep2(selectedServices);

    if (updatedCart) {
      // Scroll to top of page
      window.scrollTo({ top: 0, behavior: "smooth" });
      // Navigate directly to Step 3 (which is now the payment details step)
      onStepComplete(updatedCart, 3);
    } else {
      console.error("Failed to update cart, cannot proceed to next step");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    if (dateString === "Today") {
      return moment().format("dddd, MMM Do YYYY");
    }
    if (dateString === "Tomorrow") {
      return moment().add(1, "day").format("dddd, MMM Do YYYY");
    }
    // Handle custom date format "Wednesday 1st October"
    if (dateString.includes(",") && dateString.includes("2025")) {
      return moment(dateString, "dddd, MMMM Do YYYY").format(
        "dddd, MMM Do YYYY"
      );
    }
    // Handle short format "Wednesday 1st October"
    if (
      dateString.includes("st") ||
      dateString.includes("nd") ||
      dateString.includes("rd") ||
      dateString.includes("th")
    ) {
      return dateString;
    }
    return dateString;
  };

  const formatTime = (timeSlot) => formatArrivalTime(timeSlot);

  const getLocationName = () => {
    const selectedLocation = cartData?.step1Data?.selectedLocation;
    if (selectedLocation) {
      console.log("DEBUG: Selected location data:", selectedLocation);
      console.log("DEBUG: seoName:", selectedLocation.seoName);
      console.log("DEBUG: name:", selectedLocation.name);
      // Use seoName if available, otherwise fallback to name
      return (
        selectedLocation.seoName || selectedLocation.name || "Selected Location"
      );
    }
    return "Selected Location";
  };

  // Calculate discount for a service based on offer
  // Function to check if booking date is after offer expiry
  const checkOfferExpiry = (offer, bookingDate) => {
    if (!offer || !offer.endDate || !bookingDate) {
      return { isValid: true }; // No validation needed
    }

    // Parse the booking date
    let parsedBookingDate;
    if (bookingDate === "Today") {
      parsedBookingDate = moment().startOf("day");
    } else if (bookingDate === "Tomorrow") {
      parsedBookingDate = moment().add(1, "day").startOf("day");
    } else if (bookingDate.includes(",") && (bookingDate.includes("st") || bookingDate.includes("nd") || bookingDate.includes("rd") || bookingDate.includes("th"))) {
      // Format like "Wednesday, January 14th 2026"
      parsedBookingDate = moment(bookingDate, "dddd, MMMM Do YYYY");
    } else {
      parsedBookingDate = moment(bookingDate, "YYYY-MM-DD");
    }

    if (!parsedBookingDate.isValid()) {
      return { isValid: true }; // Can't parse date, skip validation
    }

    // Parse offer end date
    const offerEndDate = moment(offer.endDate).endOf("day");

    // Check if booking date is after offer expiry
    if (parsedBookingDate.isAfter(offerEndDate)) {
      return {
        isValid: false,
        offerEndDate: offerEndDate.format("MMMM Do, YYYY"),
        bookingDate: parsedBookingDate.format("MMMM Do, YYYY"),
      };
    }

    return { isValid: true };
  };

  const calculateServiceDiscount = (service) => {
    const offer = cartData?.offerDetails;
    const selectedDate = cartData?.step1Data?.selectedDate;
    
    // Check if booking date is after offer expiry
    if (offer && selectedDate) {
      const expiryCheck = checkOfferExpiry(offer, selectedDate);
      if (expiryCheck && !expiryCheck.isValid) {
        // Booking date is after offer expiry - don't apply discount
        console.log("Offer expires before booking date, discount not applied");
        return null;
      }
    }
    if (!offer) {
      // Only log once to avoid spam
      if (!calculateServiceDiscount._loggedNoOffer) {
        console.log("No offer found in cartData. Cart data:", cartData);
        calculateServiceDiscount._loggedNoOffer = true;
      }
      return null;
    }
    // Reset the flag if offer exists
    calculateServiceDiscount._loggedNoOffer = false;

    console.log("=== DISCOUNT CALCULATION ===");
    console.log("Service:", service.name, "ID:", service.id, "Type:", typeof service.id);
    console.log("Offer:", offer);
    console.log("Offer.services:", offer.services);
    console.log("Offer.service:", offer.service);

    const selectedLocationId = cartData?.step1Data?.selectedLocationId;

    // Check if offer applies to this service
    let offerApplies = false;

    if (offer.service == null || offer.service === null) {
      // Offer applies to multiple services or all services
      if (offer.services && offer.services.length > 0) {
        // Normalize all service IDs to strings for comparison
        // Offer services come from the API as Service objects with _id fields (populated by backend)
        const offerServiceIds = offer.services.map((s, index) => {
          // Handle Service objects from API (they have _id as ObjectId or string)
          if (s && typeof s === 'object') {
            // Service object from database has _id field
            const id = s._id ? String(s._id) : (s.id ? String(s.id) : String(s));
            console.log(`  Offer service[${index}]:`, s.name || 'unnamed', "ID:", id, "Raw:", s);
            return id;
          }
          // Handle if it's already a string or ObjectId
          const id = String(s);
          console.log(`  Offer service[${index}] (primitive):`, id);
          return id;
        });
        
        // Service ID from the services endpoint is already a string
        const serviceIdStr = String(service.id);
        
        console.log("=== DISCOUNT CHECK DEBUG ===");
        console.log("Service:", service.name);
        console.log("Service ID (from services endpoint):", serviceIdStr, typeof serviceIdStr);
        console.log("Offer services array:", offer.services);
        console.log("Offer service IDs (extracted _id):", offerServiceIds);
        console.log("Offer service IDs types:", offerServiceIds.map(id => typeof id));
        
        // Check if any offer service ID matches
        const matches = offerServiceIds.some(offerId => {
          const normalizedOfferId = String(offerId).trim();
          const normalizedServiceId = serviceIdStr.trim();
          const match = normalizedOfferId === normalizedServiceId;
          console.log(`  Comparing: "${normalizedOfferId}" === "${normalizedServiceId}" = ${match}`);
          return match;
        });
        
        if (matches) {
          offerApplies = true;
          console.log("✓ MATCH! Service qualifies for discount");
        } else {
          console.log("✗ NO MATCH - Service does not qualify");
        }
        console.log("=== END DEBUG ===");
      } else {
        // No service restriction, applies to all
        offerApplies = true;
        console.log("No service restriction, offer applies to all");
      }
    } else {
      // Offer applies to specific service
      const offerServiceId = offer.service._id ? String(offer.service._id) : String(offer.service);
      const serviceIdStr = String(service.id);
      console.log("Comparing single service - Offer:", offerServiceId, "Service:", serviceIdStr);
      if (serviceIdStr === offerServiceId) {
        offerApplies = true;
        console.log("Service matches offer.service");
      }
    }

    // Check location restrictions if offer has them
    if (offerApplies) {
      if (offer.location || (offer.locations && offer.locations.length > 0)) {
        let locationMatches = false;
        
        if (offer.locations && offer.locations.length > 0) {
          const offerLocationIds = offer.locations.map((l) =>
            String(l._id || l)
          );
          console.log("Offer location IDs:", offerLocationIds);
          console.log("Selected location ID:", String(selectedLocationId));
          if (selectedLocationId && offerLocationIds.includes(String(selectedLocationId))) {
            locationMatches = true;
            console.log("Location matches offer locations array");
          }
        } else if (offer.location) {
          const offerLocationId = String(offer.location._id || offer.location);
          if (selectedLocationId && String(selectedLocationId) === offerLocationId) {
            locationMatches = true;
            console.log("Location matches offer.location");
          }
        }
        
        if (!locationMatches) {
          console.log("Location does not match offer restrictions");
          offerApplies = false;
        }
      } else {
        console.log("No location restrictions on offer");
      }
    }

    if (!offerApplies) {
      console.log("Offer does not apply to this service");
      return null;
    }

    console.log("Offer applies! Calculating discount...");

    const basePrice = service.price;
    let discountAmount = 0;
    let discountText = "";

    if (offer.type === "fixed") {
      discountAmount = offer.value;
      discountText = `$${offer.value} off`;
    } else if (offer.type === "percent") {
      discountAmount = (basePrice * offer.value) / 100;
      discountText = `${offer.value}% off`;
    } else {
      return null; // nodiscount type
    }

    const discountedPrice = Math.max(0, basePrice - discountAmount);

    return {
      originalPrice: basePrice,
      discountedPrice: discountedPrice,
      discountAmount: discountAmount,
      discountText: discountText,
    };
  };

  const step2BookingDateIso = selectedBookingDateToIso(
    cartData?.step1Data?.selectedDate,
  );
  const step2PublicHolidayName =
    step2BookingDateIso && publicHolidayByDate[step2BookingDateIso]
      ? publicHolidayByDate[step2BookingDateIso]
      : null;
  const step2IsPublicHoliday = Boolean(step2PublicHolidayName);

  const serviceFinalPriceWithPublicHoliday = (service) => {
    const discount = calculateServiceDiscount(service);
    const subtotalAfterOffer = discount
      ? discount.discountedPrice
      : service.price;
    const phExtra = computePublicHolidaySurchargeAmount(
      subtotalAfterOffer,
      step2IsPublicHoliday,
    );
    return Math.round((subtotalAfterOffer + phExtra) * 100) / 100;
  };

  const getServiceGroups = () => {
    if (!services) return [];

    const serviceGroups = [];

    if (services.washServices && services.washServices.length > 0) {
      serviceGroups.push({
        id: "wash-group",
        category: "washServices",
        categoryName: "Wash",
        services: services.washServices,
        minPrice: Math.min(
          ...services.washServices.map((s) =>
            serviceFinalPriceWithPublicHoliday(s),
          ),
        ),
      });
    }

    if (services.detailServices && services.detailServices.length > 0) {
      serviceGroups.push({
        id: "detail-group",
        category: "detailServices",
        categoryName: "Detail",
        services: services.detailServices,
        minPrice: Math.min(
          ...services.detailServices.map((s) =>
            serviceFinalPriceWithPublicHoliday(s),
          ),
        ),
      });
    }

    return serviceGroups;
  };

  const renderServiceCard = (
    service,
    category,
    isSelected = false,
    isFeatured = false
  ) => {
    const cardClasses = `service-card ${isSelected ? "selected" : ""} ${
      isFeatured ? "featured" : ""
    }`;

    const discount = calculateServiceDiscount(service);
    const subtotalAfterOffer = discount
      ? discount.discountedPrice
      : service.price;
    const phExtra = computePublicHolidaySurchargeAmount(
      subtotalAfterOffer,
      step2IsPublicHoliday,
    );
    const displayPrice =
      Math.round((subtotalAfterOffer + phExtra) * 100) / 100;
    const strikeThroughPrice = discount
      ? discount.originalPrice
      : step2IsPublicHoliday
        ? service.price
        : null;

    if (discount) {
      console.log(`🎉 DISCOUNT APPLIED to ${service.name}:`, discount);
    }

    return (
      <div
        key={service.id}
        className={cardClasses}
        onClick={() => handleServiceSelect(service, category)}
      >
        {service.isBestValue && (
          <div className="best-value-badge">Best value</div>
        )}

        <div className="service-header">
          <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
            {discount && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
                <div className="discount-badge" style={{
                  background: "#ef4444",
                  color: "white",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                  fontWeight: "600",
                  width: "auto"
                }}>
                  {discount.discountText}
                </div>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <h4 className="service-name">{service.name}</h4>
              <div className="service-price-section">
                <div className="service-price-row">
                  <div className="service-price">
                    {strikeThroughPrice != null && (
                      <span className="original-price" style={{
                        textDecoration: "line-through",
                        color: "#9ca3af",
                        fontSize: "0.9em",
                        marginRight: "8px"
                      }}>
                        ${strikeThroughPrice.toFixed(2)}
                      </span>
                    )}
                    ${displayPrice.toFixed(2)}
                  </div>
                  <div className="service-indicator">{isSelected ? "✓" : ""}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="service-duration">{service.duration}</div>

        <p className="service-description">{service.description}</p>

        {service.includedServices && service.includedServices.length > 0 && (
          <div className="included-services">
            <ul>
              {service.includedServices.map((item, idx) => {
                console.log(
                  `Service: ${service.name}, Included Item ${idx}:`,
                  item
                );
                return <li key={idx}>{item}</li>;
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const scrollToService = (direction) => {
    const serviceGroups = getServiceGroups();
    console.log(
      "scrollToService called:",
      direction,
      "currentServiceIndex:",
      currentServiceIndex,
      "serviceGroups.length:",
      serviceGroups.length
    );

    // Mark that user has manually navigated
    setHasManuallyNavigated(true);

    if (direction === "prev") {
      const newIndex = Math.max(0, currentServiceIndex - 1);
      console.log("Previous navigation: newIndex:", newIndex);
      setCurrentServiceIndex(newIndex);
    } else {
      const newIndex = Math.min(
        serviceGroups.length - 1,
        currentServiceIndex + 1
      );
      console.log("Next navigation: newIndex:", newIndex);
      setCurrentServiceIndex(newIndex);
    }
  };

  // Touch/Swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      scrollToService("next");
    } else if (isRightSwipe) {
      scrollToService("prev");
    }
  };

  const getCurrentServiceInfo = () => {
    const serviceGroups = getServiceGroups();
    if (serviceGroups.length === 0) return { name: "No Services", price: 0 };

    const currentGroup = serviceGroups[currentServiceIndex];
    if (!currentGroup) return { name: "No Services", price: 0 };

    return {
      name: currentGroup.categoryName,
      price: currentGroup.minPrice,
    };
  };

  const renderServices = () => {
    console.log(
      "renderServices - currentServiceIndex:",
      currentServiceIndex,
      "selectedServices:",
      selectedServices
    );

    if (isLoadingServices) {
      return (
        <div className="loading-message">Loading available services...</div>
      );
    }
    if (!services) {
      return <div className="no-services">No services available.</div>;
    }

    const serviceGroups = getServiceGroups();
    const currentGroup = serviceGroups[currentServiceIndex];

    console.log("serviceGroups:", serviceGroups, "currentGroup:", currentGroup);

    if (!currentGroup) {
      return <div className="no-services">No services available.</div>;
    }

    return (
      <div className="services-container">
        <div
          className="services-slider"
          style={{
            transform: `translateX(-${currentServiceIndex * 100}%)`,
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {serviceGroups.map((group, index) => (
            <div
              key={group.id}
              className={`service-slide ${
                index === currentServiceIndex ? "active" : ""
              }`}
            >
              <div className="service-group-header">
                <h3>{group.categoryName} Services</h3>
              </div>
              <div className="section-divider"></div>
              <div className="service-cards-grid">
                {group.services.map((service) => {
                  const isSelected = selectedServices.some(
                    (s) => s.id === service.id && s.category === group.category
                  );
                  return (
                    <div key={service.id} className="service-card-wrapper">
                      {renderServiceCard(
                        service,
                        group.category,
                        isSelected,
                        service.isBestValue
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const serviceGroups = getServiceGroups();
  const lastServiceGroupIndex = Math.max(0, serviceGroups.length - 1);
  const washGroup = serviceGroups[0];
  const detailGroup = serviceGroups[1];

  return (
    <div className="step2-container">
      {/* Main Content - Centered */}
      <div className="step2-content">
        {/* Logo */}
        <div className="step2-header">
          <div className="logo">
            <img
              src={ConciergeLogo}
              alt="Concierge Car Wash"
              className="logo-image"
            />
          </div>
        </div>

        {/* Selected Details Summary */}
        <div className="selected-details-summary">
          <div className="detail-section">
            <h4 className="detail-heading">Your Expected Arrival Time</h4>
            <p>{formatTime(cartData?.step1Data?.selectedTimeSlot)}</p>
            <p>{formatDate(cartData?.step1Data?.selectedDate)}</p>
          </div>

          <div className="detail-section">
            <h4 className="detail-heading">Selected Store</h4>
            <p>{getLocationName()}</p>
          </div>
        </div>

        {/* Change Button */}
        <div className="change-button-container">
          <a
            href="#"
            className="made-mistake-link"
            onClick={(e) => {
              e.preventDefault();
              onStepBack();
            }}
          >
            Made a mistake? go back
          </a>
        </div>

        {/* Divider */}
        <div className="section-divider"></div>

        {/* Service Selection */}
        <div className={`service-selection ${serviceError ? "has-error" : ""}`}>
          {serviceError && <div className="booking-flow-error-message">{serviceError}</div>}
          <h4 className="detail-heading">Select your Service</h4>
          {step2PublicHolidayName ? (
            <div className="public-holiday-surcharge-notice" role="status">
              <strong>{step2PublicHolidayName}</strong>
              <span> — {PUBLIC_HOLIDAY_SURCHARGE_NOTICE}</span>
              <div className="step2-ph-pricing-hint">
                Prices shown include the 10% public holiday surcharge.
              </div>
            </div>
          ) : null}

          {/* Service Navigation */}
          <div className="service-navigation">
            {/* Wash Button - Always show */}
            <button
              className={`service-nav-button ${
                currentServiceIndex === 0 ? "inactive" : ""
              }`}
              onClick={() => {
                console.log(
                  "Wash button clicked, currentServiceIndex:",
                  currentServiceIndex
                );
                if (currentServiceIndex !== 0 && washGroup) {
                  setCurrentServiceIndex(0);
                  setHasManuallyNavigated(true);
                }
              }}
              disabled={currentServiceIndex === 0 || !washGroup}
            >
              <span className="nav-arrow">&lt;</span>
              <span className="nav-text">
                <span className="nav-category">Wash</span>
                <span className="nav-price">
                  {washGroup
                    ? `From $${Number(washGroup.minPrice).toFixed(2)}`
                    : "N/A"}
                </span>
              </span>
            </button>
            {/* Detail Button - Always show */}
            <button
              className={`service-nav-button ${
                currentServiceIndex === 1 ? "inactive" : ""
              }`}
              onClick={() => {
                console.log(
                  "Detail button clicked, currentServiceIndex:",
                  currentServiceIndex
                );
                if (currentServiceIndex !== 1 && detailGroup) {
                  setCurrentServiceIndex(1);
                  setHasManuallyNavigated(true);
                }
              }}
              disabled={currentServiceIndex === 1 || !detailGroup}
            >
              <span className="nav-text">
                <span className="nav-category">Detail</span>
                <span className="nav-price">
                  {detailGroup
                    ? `From $${Number(detailGroup.minPrice).toFixed(2)}`
                    : "N/A"}
                </span>
              </span>
              <span className="nav-arrow">&gt;</span>
            </button>
          </div>

          {renderServices()}

          {/* Select Service Button - Inside Services Container */}
          <div className="step2-actions">
            <button
              className="continue-button"
              onClick={handleContinue}
              disabled={isLoading || selectedServices.length === 0}
            >
              {selectedServices.length > 0
                ? `Select ${selectedServices[0].name}`
                : "Select Service"}
            </button>
          </div>
        </div>
      </div>

      {/* Alert Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        showCancel={modal.showCancel}
        onConfirm={modal.onConfirm}
      />
    </div>
  );
};

export default Step2;
